import React from 'react';
import { CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import formatAmountWithDollar from 'Feature/V2/HbtPortfolioManagement/utils/FormatAmountWithDollar';
import { formatRatio } from 'Feature/V2/HbtPortfolioManagement/utils/FormatRatio';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { LoanValidationDetailsType } from '../../api/types';
import ErrorMessage, { SpinnerElement } from '../ErrorMessage';
import styles from './styles.module.scss';
import LoanStatusPill from 'Feature/V2/HbtMasterPool/shared/LoanStatusPill';
import { loanStatusMapping } from 'Feature/V2/HbtMasterPool/components/HbtMasterPoolTable/utils/LoanStatusMapping';
import { getLoanStatusLabel } from 'Feature/V2/HbtMasterPool/components/HbtMasterPoolDetails/utils/masterPoolDetailsUtils';
import { getLoanStatusLabel as getPortfolioLoanStatusLabel } from '../HbtPortfolioLoansInventoryTable/columns/loansInventoryTableCellRenderers';
import { loanStatusMapping as portfolioLoanStatusLabel } from '../HbtPortfolioLoansInventoryTable/utils/LoanStatusMapping';
import PortfolioLoanStatusPill from '../HbtPortfolioLoansInventoryTable/LoanStatusPill';

interface ApplicationOverviewDetailsProps {
  showSpinner?: boolean;
  showErrorMessage?: boolean;
  className?: string;
  loanSummaryError: string;
  data?: LoanValidationDetailsType[];
  isCancellationLoan: boolean;
  title?: string;
  isResultsCollapsed?: boolean;
  isMasterPoolLoanComposition?: boolean;
}

export const ApplicationLoanCompositionDetails = ({
  showSpinner,
  showErrorMessage,
  className,
  data,
  isCancellationLoan,
  loanSummaryError,
  isResultsCollapsed,
  title,
  isMasterPoolLoanComposition = false
}: ApplicationOverviewDetailsProps) => {
  const { t: tPortfolio } = useTranslation('HbtPortfolioManagementRequestOverview');
  const { t: tMasterPool } = useTranslation('HbtMasterPool');

  const headingId = 'application-loan-composition-heading';

  return (
    <Box
      tabIndex={0}
      className={`${styles.validationResults} ${className}`}
      aria-labelledby={headingId}
    >
      {showSpinner && <SpinnerElement />}
      {showErrorMessage && <ErrorMessage errorType={loanSummaryError} />}

      {data && data.length > 0 && loanSummaryError === '' && (
        <Box className={styles.theContent}>
          <Typography id={headingId} variant="h4" className={styles.headerText}>
            {title}
          </Typography>

          <Box className={`${styles.contentWrapper} ${isResultsCollapsed ? 'collapsed' : ''}`}>
            <Box className={styles.tableContainer}>
              <TableContainer>
                <Table aria-label={tPortfolio('Loan Composition Table')}>
                  <TableHead>
                    <TableRow>
                      <TableCell scope="col" className={styles.tableCell}>
                        {tPortfolio('PI-Portfolio-Management-Request-Overview-Loan-Status')}
                      </TableCell>
                      <TableCell scope="col" align="right" className={styles.tableCell}>
                        {tPortfolio('PI-Portfolio-Management-Request-Overview-Count')}
                      </TableCell>
                      <TableCell scope="col" align="right" className={styles.tableCell}>
                        {`% (${tPortfolio('PI-Portfolio-Management-Request-Overview-Count')})`}
                      </TableCell>
                      {!isCancellationLoan && (
                        <>
                          <TableCell scope="col" align="right" className={styles.tableCell}>
                            {tPortfolio('PI-Portfolio-Management-Request-Overview-Amount')}
                          </TableCell>
                          <TableCell scope="col" align="right" className={styles.tableCell}>
                            {`% (${tPortfolio('PI-Portfolio-Management-Request-Overview-Amount')})`}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {data.map((row, index) => {
                      const totalTooltipId = `total-tooltip-${index}`;
                      return (
                        <TableRow
                          key={index}
                          sx={{
                            borderTop:
                              index === data.length - 1
                                ? '2px solid rgba(224, 224, 224, 1)'
                                : 'none'
                          }}
                        >
                          <TableCell
                            className={`${styles.noBottomBorder} ${
                              index === data.length - 1 ? styles.tableCell : ''
                            }`}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {index !== data.length - 1 && (
                                <Box className={styles.statusPillWrapperLoansComposition}>
                                  {isMasterPoolLoanComposition ? (
                                    <LoanStatusPill
                                      filled={false}
                                      status={
                                        data[index].loanStatusCode as keyof typeof loanStatusMapping
                                      }
                                      label={getLoanStatusLabel(
                                        data[index]
                                          .loanStatusCode as keyof typeof loanStatusMapping,
                                        tMasterPool
                                      )}
                                    />
                                  ) : (
                                    <PortfolioLoanStatusPill
                                      filled={false}
                                      status={
                                        data[index]
                                          .loanStatus as keyof typeof portfolioLoanStatusLabel
                                      }
                                      label={getPortfolioLoanStatusLabel(
                                        data[index]
                                          .loanStatus as keyof typeof portfolioLoanStatusLabel,
                                        tPortfolio
                                      )}
                                    />
                                  )}
                                </Box>
                              )}

                              {row.loanStatusName === 'Total' && (
                                <div className={styles.totalLoansContainer}>
                                  <span id={totalTooltipId}>{row.loanStatusName}</span>
                                  <Tooltip
                                    title={tPortfolio('Total-Tool-Tip')}
                                    aria-describedby={totalTooltipId}
                                    className={styles.totalAmountTooltip}
                                    placement="top-start"
                                    arrow
                                    slotProps={{
                                      tooltip: {
                                        className: styles.requestOverviewDetailsToolTip
                                      },
                                      arrow: {
                                        className: styles.tooltipArrow
                                      }
                                    }}
                                  >
                                    <div>
                                      <CmhcIcon
                                        size={CmhcIconSize.SMALL}
                                        type="icon_info_outlined"
                                        aria-hidden="true"
                                      />
                                    </div>
                                  </Tooltip>
                                </div>
                              )}
                            </Box>
                          </TableCell>

                          <TableCell className={styles.noBottomBorder} align="right">
                            <span>{row.count?.toLocaleString(`${i18n.language}-CA`)}</span>
                          </TableCell>

                          <TableCell className={styles.noBottomBorder} align="right">
                            <span>{formatRatio(+row.countPercentage, i18n.language)}</span>
                          </TableCell>

                          {!isCancellationLoan && (
                            <>
                              <TableCell className={styles.noBottomBorder} align="right">
                                <span>
                                  {formatAmountWithDollar(
                                    row.amount?.toString() ?? '',
                                    i18n.language
                                  )}
                                </span>
                              </TableCell>
                              <TableCell className={styles.noBottomBorder} align="right">
                                <span>{formatRatio(+row.amountPercentage, i18n.language)}</span>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
